import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { GlassCard } from './Layout';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Search, ExternalLink, MapPin, ListChecks, Globe, ShieldAlert, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/mentors`;

export const MentorFinder = ({ onDraftFor }) => {
  const { getAuthHeader } = useAuth();
  const [f, setF] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(null);
  const [liveBusy, setLiveBusy] = useState(false);
  const [liveAvailable, setLiveAvailable] = useState(false);
  const [form, setForm] = useState({ role: 'industry_mentor', industry: '', state: '', city: '', seniority: 'mid', backgrounds: [] });

  const load = useCallback(async () => {
    const [r, ls] = await Promise.all([
      axios.get(`${API}/filters`, getAuthHeader()),
      axios.get(`${API}/live-status`, getAuthHeader()),
    ]);
    setF(r.data);
    setLiveAvailable(!!ls.data.available);
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const search = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/search`, form, getAuthHeader());
      setRes(r.data);
    } finally { setBusy(false); }
  };

  const searchLive = async () => {
    setLiveBusy(true);
    try {
      const r = await axios.post(`${API}/search/live`, form, { ...getAuthHeader(), timeout: 120000 });
      setLive(r.data);
      if (!r.data.mentors?.length) toast.info('No one met the bar this time. Widen the location or seniority.');
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : det?.message || 'Live search failed.');
    } finally { setLiveBusy(false); }
  };

  if (!f) return null;
  const toggleBg = (k) => setForm(s => ({
    ...s, backgrounds: s.backgrounds.includes(k) ? s.backgrounds.filter(x => x !== k) : [...s.backgrounds, k],
  }));

  return (
    <GlassCard className="fp-rail" data-testid="mentor-finder">
      <h3 className="text-base md:text-lg font-semibold text-white flex items-center gap-2">
        <Search className="w-4 h-4 text-cyan-300" /> Find the kind of mentor you need
      </h3>
      <p className="text-xs text-slate-300 mt-1">{f.note}</p>

      <div className="grid md:grid-cols-3 gap-3 mt-4">
        <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
          className="input-dark" data-testid="mentor-role-select">
          {f.roles.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        <select value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })}
          className="input-dark" data-testid="mentor-industry-select">
          <option value="">Any industry</option>
          {f.industries.map(i => <option key={i} value={i}>{i}</option>)}
        </select>
        <select value={form.seniority} onChange={e => setForm({ ...form, seniority: e.target.value })}
          className="input-dark" data-testid="mentor-seniority-select">
          {f.seniority.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <Input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })}
          placeholder="State (e.g. New Jersey)" data-testid="mentor-state-input" />
        <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
          placeholder="City (optional)" data-testid="mentor-city-input" />
      </div>

      <p className="text-[11px] uppercase tracking-wider text-slate-300 mt-4 mb-2">Anything you share with them</p>
      <div className="flex flex-wrap gap-2">
        {f.backgrounds.map(b => {
          const on = form.backgrounds.includes(b.key);
          return (
            <button key={b.key} onClick={() => toggleBg(b.key)} data-testid={`mentor-bg-${b.key}`}
              className="fp-pill" style={on ? { borderColor: '#22d3ee', background: 'rgba(34,211,238,.14)', color: '#fff' } : undefined}>
              {on ? '✓ ' : ''}{b.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-4">
        <Button className="btn-primary" onClick={search} disabled={busy} data-testid="mentor-search-btn">
          {busy ? 'Searching…' : 'Show me where these people are'}
        </Button>
        <Button variant="outline" onClick={searchLive} disabled={liveBusy || !liveAvailable} data-testid="mentor-live-search-btn">
          {liveBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Searching the web…</> : <><Globe className="w-4 h-4 mr-2" />Find real people</>}
        </Button>
        <span className="text-[11px] text-slate-300">
          {liveAvailable
            ? 'Live web search names real professionals and shows the sources. Uses AI.'
            : 'Live people-search is not switched on yet, the button above still tells you exactly where to look.'}
        </span>
      </div>

      {live && (
        <div className="mt-6" data-testid="mentor-live-results">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <p className="text-[11px] uppercase tracking-wider text-emerald-300 flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5" /> Real people we found ({live.count})
            </p>
            <span className="text-[11px] text-slate-300">
              {live.cached ? 'from a search in the last 24h' : `searched ${new Date(live.searched_at).toLocaleString()}`}
            </span>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {(live.mentors || []).map((m, i) => (
              <div key={`${m.name}-${i}`} className="fp-tile p-4" data-testid={`live-mentor-${i}`}>
                <p className="text-sm font-semibold text-white">{m.name}</p>
                <p className="text-xs text-cyan-300 mt-0.5">{m.current_role}{m.organization ? ` · ${m.organization}` : ''}</p>
                <p className="text-[11px] text-slate-300">{m.location}</p>
                <p className="text-xs text-slate-300 mt-2">{m.why_match}</p>
                <p className="text-xs text-slate-300 mt-2"><span className="text-slate-300">Reach them via, </span>{m.how_to_reach}</p>
                <button onClick={() => onDraftFor?.(form.role)} data-testid={`live-draft-${i}`}
                  className="mt-3 text-xs text-cyan-300 hover:text-cyan-200 transition-colors duration-200">
                  Draft a message to them →
                </button>
              </div>
            ))}
          </div>

          {(live.sources || []).length > 0 && (
            <div className="mt-4">
              <p className="text-[11px] uppercase tracking-wider text-slate-300 mb-2">Sources</p>
              <div className="flex flex-wrap gap-1.5">
                {live.sources.map((s, i) => (
                  <a key={i} href={s.url} target="_blank" rel="noreferrer" className="fp-pill" data-testid={`live-source-${i}`}>
                    <ExternalLink className="w-3 h-3" />{(s.title || s.url || '').slice(0, 48)}
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 p-3 rounded-lg border border-amber-400/40 bg-amber-500/10">
            <p className="text-[11px] uppercase tracking-wider text-amber-300 flex items-center gap-1.5 mb-1.5">
              <ShieldAlert className="w-3.5 h-3.5" /> Check before you trust it
            </p>
            <ul className="space-y-1">
              {(live.verify || []).map((v, i) => <li key={i} className="text-xs text-amber-100/90">- {v}</li>)}
            </ul>
            <p className="text-[11px] text-slate-300 mt-2">{live.disclosure}</p>
          </div>
        </div>
      )}

      {res && (
        <div className="mt-6 space-y-5" data-testid="mentor-results">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-cyan-300 mb-2">Pre-built searches</p>
            <div className="grid md:grid-cols-2 gap-3">
              {res.searches.map(s => (
                <a key={s.label} href={s.url} target="_blank" rel="noreferrer" className="fp-tile p-3 block"
                  data-testid={`mentor-search-link-${s.label}`}>
                  <p className="text-sm text-white flex items-center gap-1.5">{s.label}<ExternalLink className="w-3 h-3 text-slate-300" /></p>
                  <p className="text-xs text-slate-300 mt-1">{s.how}</p>
                </a>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-wider text-violet-300 mb-2 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" /> Where this kind of mentor concentrates
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              {res.venues.map(v => (
                <div key={v.name} className="fp-tile p-3">
                  <p className="text-sm text-white">{v.name}</p>
                  <p className="text-xs text-slate-300 mt-1">{v.why}</p>
                  <p className="text-[10px] uppercase tracking-wider text-slate-300 mt-2">{v.kind}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div className="fp-tile p-4">
              <p className="text-[11px] uppercase tracking-wider text-slate-300 flex items-center gap-1.5 mb-2">
                <ListChecks className="w-3.5 h-3.5 text-emerald-300" /> Screen them before you message
              </p>
              <ul className="space-y-1">
                {res.screening.map((s, i) => <li key={i} className="text-xs text-slate-300 flex gap-2"><span className="text-emerald-400">•</span>{s}</li>)}
              </ul>
            </div>
            <div className="fp-tile p-4">
              <p className="text-[11px] uppercase tracking-wider text-slate-300 mb-2">How to approach them</p>
              <ol className="space-y-1.5">
                {res.cadence.map((c, i) => (
                  <li key={i} className="text-xs text-slate-300">
                    <span className="text-cyan-300">{i + 1}. {c.step}</span>, {c.detail}
                  </li>
                ))}
              </ol>
              <button onClick={() => onDraftFor?.(form.role)} data-testid="mentor-draft-btn"
                className="mt-3 text-xs text-cyan-300 hover:text-cyan-200 transition-colors duration-200">
                Draft my opening message for this role →
              </button>
            </div>
          </div>
          <p className="text-[11px] text-slate-300">{res.note}</p>
        </div>
      )}
    </GlassCard>
  );
};

export default MentorFinder;
